import React from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

interface StepItem {
  id: string;
  label: string;
  badge: string;
  status: "completed" | "current" | "upcoming";
}

const STEPS: StepItem[] = [
  {
    id: "class-actions",
    label: "Class action claims",
    badge: "Up to $1087",
    status: "completed",
  },
  {
    id: "unclaimed-money",
    label: "Unclaimed money\nsearch",
    badge: "Average $32 one-time",
    status: "current",
  },
  {
    id: "ai-refund",
    label: "AI refund assistant",
    badge: "Up to $31/year",
    status: "upcoming",
  },
  {
    id: "money-tracking",
    label: "Money tracking",
    badge: "Up to $53/mo",
    status: "upcoming",
  },
];

const UnclaimedMoneySearchScreen = () => {
  const handleContinue = () => {
    router.push("/signup/unclaimed-money-address");
  };

  const renderStep = (step: StepItem, index: number) => {
    const isCompleted = step.status === "completed";
    const isCurrent = step.status === "current";
    const isLast = index === STEPS.length - 1;

    if (isCompleted) {
      return (
        <View key={step.id} style={styles.completedStepRow}>
          <Ionicons name="checkmark-sharp" size={22} color="#0F9129" style={{ fontWeight: 'bold' }} />
          <Text style={styles.stepLabel}>{step.label}</Text>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{step.badge}</Text>
          </View>
        </View>
      );
    }

    if (isCurrent) {
      return (
        <View key={step.id} style={styles.currentStepRow}>
          {/* Left indicator with line and dot */}
          <View style={styles.indicatorColumn}>
            {/* The dot sits at top, line extends below */}
            <View style={styles.currentDot} />
            {!isLast && <View style={styles.verticalLine} />}
          </View>
          {/* Right content - the card */}
          <View style={styles.currentStepCard}>
            <Text style={styles.nextLabel}>Next</Text>
            <View style={styles.currentStepContent}>
              <Text style={styles.currentStepLabel}>{step.label}</Text>
              <View style={styles.currentBadge}>
                <Text style={styles.badgeText}>{step.badge}</Text>
              </View>
            </View>
          </View>
        </View>
      );
    }

    // Upcoming step
    return (
      <View key={step.id} style={styles.upcomingStepRow}>
        {/* Left indicator: line connects from above, dot, line below */}
        <View style={styles.indicatorColumn}>
          <View style={styles.connectorLine} />
          <View style={styles.upcomingDot} />
          {!isLast && <View style={styles.verticalLine} />}
        </View>
        {/* Right content */}
        <View style={styles.upcomingStepContent}>
          <Text style={[styles.stepLabel, styles.upcomingText]}>{step.label}</Text>
          <View style={styles.badge}>
            <Text style={[styles.badgeText, styles.upcomingText]}>{step.badge}</Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View style={styles.progressFill} />
          </View>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.headerSection}>
            <Text style={styles.title}>
              Next, let's search for{"\n"}unclaimed money
            </Text>
            <Text style={styles.subtitle}>
              In just a few steps, you'll unlock all your benefits and start
              saving money.
            </Text>
          </View>

          {/* Steps */}
          <View style={styles.stepsContainer}>
            {STEPS.map((step, index) => renderStep(step, index))}
          </View>
        </View>

        {/* Spacer */}
        <View style={styles.spacer} />

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          <View style={styles.buttonContainer}>
            <ActionButton title="Continue" onPress={handleContinue} />
          </View>
          <Text style={styles.disclaimer}>*All numbers are estimated</Text>
        </View>
      </SafeAreaView>
    </View>
  );
};

export default UnclaimedMoneySearchScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  progressContainer: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 16,
  },
  progressBar: {
    height: 6,
    backgroundColor: "#E1EFE4",
    borderRadius: 100,
  },
  progressFill: {
    width: 16,
    height: 6,
    backgroundColor: "rgba(22, 29, 20, 0.5)",
    borderRadius: 100,
  },
  content: {
    paddingHorizontal: 16,
  },
  headerSection: {
    gap: 12,
    marginTop: 5,
    marginBottom: 24,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    letterSpacing: -0.64,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: -0.32,
  },
  stepsContainer: {
    gap: 0,
  },
  completedStepRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 21,
  },
  stepLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
  },
  badge: {
    backgroundColor: "#E1EFE4",
    paddingHorizontal: 8,
    borderRadius: 100,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  badgeText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 12,
    lineHeight: 25,
    color: "#161D14",
    letterSpacing: -0.24,
    textAlign: "center",
  },
  currentStepRow: {
    flexDirection: "row",
  },
  indicatorColumn: {
    width: 24,
    alignItems: "center",
  },
  currentDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#0F9129",
  },
  verticalLine: {
    width: 2,
    flex: 1,
    backgroundColor: "#A3A3A3",
  },
  connectorLine: {
    width: 2,
    height: 27,
    backgroundColor: "#A3A3A3",
  },
  currentStepCard: {
    flex: 1,
    backgroundColor: "#F3F7F3",
    borderRadius: 8,
    padding: 16,
    gap: 2,
    marginLeft: 8,
    marginBottom: 0,
  },
  nextLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 13,
    lineHeight: 15,
    color: "#0F9129",
    letterSpacing: -0.26,
  },
  currentStepContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  currentStepLabel: {
    flex: 1,
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
  },
  currentBadge: {
    backgroundColor: "#E1EFE4",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 100,
    justifyContent: "center",
    alignItems: "center",
  },
  upcomingStepRow: {
    flexDirection: "row",
  },
  upcomingDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#FFFFFF",
    borderWidth: 2,
    borderColor: "#A3A3A3",
  },
  upcomingStepContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginLeft: 8,
    marginTop: 18,
    marginBottom: 8,
  },
  upcomingText: {
    opacity: 0.7,
  },
  spacer: {
    flex: 1,
  },
  bottomSection: {
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F3F3",
    paddingTop: 12,
  },
  buttonContainer: {
    marginBottom: 12,
  },
  disclaimer: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
  },
});
